import type { ComponentType, SVGProps } from 'react';
import type { IconWeight } from '@phosphor-icons/react';

/**
 * Kyra Icons: set propio de glifos (rejilla 24x24, trazo 1.75, puntas redondas).
 * Cada glifo lleva un relleno suave (`.soft`) y un nodo solido (`.node`) como firma de marca,
 * el mismo lenguaje del logo. Se usa como icono de linea (hereda `currentColor`) y dentro de
 * `ModuleTile`, el hexagono de color de cada modulo. La fuente de verdad del dibujo es este archivo.
 */
export type KyraIconName =
  | 'home'
  | 'dashboard'
  | 'summary'
  | 'treasury'
  | 'ops'
  | 'import'
  | 'wallet'
  | 'conc'
  | 'finreport'
  | 'reports'
  | 'budget'
  | 'membership'
  | 'member'
  | 'receipt'
  | 'charge'
  | 'feeimport'
  | 'bulk'
  | 'settings'
  | 'company'
  | 'access'
  | 'catalogs'
  | 'fx'
  | 'payment'
  | 'logistics'
  | 'cube'
  | 'stock'
  | 'analysis'
  | 'stocktake'
  | 'warehouse'
  | 'landing'
  | 'blog'
  | 'edit'
  | 'shield'
  | 'orgs'
  | 'users'
  | 'plans'
  | 'trend'
  | 'globe'
  | 'coin'
  | 'bank'
  | 'tree'
  | 'cards'
  | 'medal'
  | 'calendar'
  | 'fields'
  | 'sign'
  | 'doc'
  | 'hash'
  | 'register'
  | 'cart'
  | 'order'
  | 'done'
  | 'warning'
  | 'list'
  | 'lock'
  | 'income'
  | 'expense'
  | 'swap'
  | 'cheque'
  | 'creditnote'
  | 'debitnote'
  | 'hourglass'
  | 'calcheck'
  | 'useradd'
  | 'adjust';

export const KYRA_ICON_LABELS: Record<KyraIconName, string> = {
  home: 'Inicio',
  dashboard: 'Dashboard',
  summary: 'Resumen',
  treasury: 'Tesorería',
  ops: 'Operaciones',
  import: 'Importar operaciones',
  wallet: 'Cuentas',
  conc: 'Conciliaciones',
  finreport: 'Informe financiero',
  reports: 'Reportes',
  budget: 'Presupuestos',
  membership: 'Membresías',
  member: 'Miembros',
  receipt: 'Recibos de caja',
  charge: 'Cargos eventuales',
  feeimport: 'Importar cuotas',
  bulk: 'Carga masiva de cuotas',
  settings: 'General',
  company: 'Empresa',
  access: 'Usuarios y acceso',
  catalogs: 'Catálogos',
  fx: 'Tasa de cambio',
  payment: 'Formas de pago',
  logistics: 'Logística',
  cube: 'Productos y servicios',
  stock: 'Existencias',
  analysis: 'Análisis de inventario',
  stocktake: 'Tomas de inventario',
  warehouse: 'Almacenes',
  landing: 'Landing',
  blog: 'Blog',
  edit: 'Editores',
  shield: 'Superadmin',
  orgs: 'Organizaciones',
  users: 'Personas',
  plans: 'Planes comerciales',
  trend: 'Reporte comercial',
  globe: 'Países',
  coin: 'Monedas',
  bank: 'Bancos',
  tree: 'Rubros',
  cards: 'Tipos de cuenta',
  medal: 'Tipos de membresía',
  calendar: 'Tipos de cuota',
  fields: 'Datos adicionales de miembros',
  sign: 'Firmantes',
  doc: 'Tipos de operación',
  hash: 'Series de numeración',
  register: 'Caja',
  cart: 'Venta',
  order: 'Pedido',
  done: 'Listo',
  warning: 'Alerta',
  list: 'Lista',
  lock: 'Cierre',
  income: 'Depósito',
  expense: 'Retiro',
  swap: 'Entre cuentas',
  cheque: 'Cheque',
  creditnote: 'Nota de crédito',
  debitnote: 'Nota de débito',
  hourglass: 'Antigüedad',
  calcheck: 'Cuotas al día',
  useradd: 'Nuevo miembro',
  adjust: 'Ajuste',
};

const GLYPHS: Record<KyraIconName, JSX.Element> = {
  home: (
    <>
      <path className="soft" d="M4 11 12 4l8 7v8a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19z" />
      <path d="M4 11 12 4l8 7v8a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19z" />
      <circle className="node" cx="12" cy="15" r="1.7" />
    </>
  ),
  dashboard: (
    <>
      <path className="soft" d="M4 16a8 8 0 0 1 16 0z" />
      <path d="M4 16a8 8 0 0 1 16 0M12 16l3.8-5M6 19.5h12" />
      <circle className="node" cx="12" cy="16" r="1.7" />
    </>
  ),
  summary: (
    <>
      <rect className="soft" x="4" y="4" width="7" height="7" rx="1.8" />
      <rect x="4" y="4" width="7" height="7" rx="1.8" />
      <rect x="13" y="4" width="7" height="7" rx="1.8" />
      <rect x="4" y="13" width="7" height="7" rx="1.8" />
      <rect x="13" y="13" width="7" height="7" rx="1.8" />
      <circle className="node" cx="16.5" cy="16.5" r="1.3" />
    </>
  ),
  treasury: (
    <>
      <path
        className="soft"
        d="M4.5 8c0-1.7 3.4-3 7.5-3s7.5 1.3 7.5 3-3.4 3-7.5 3-7.5-1.3-7.5-3z"
      />
      <path d="M4.5 8c0-1.7 3.4-3 7.5-3s7.5 1.3 7.5 3-3.4 3-7.5 3-7.5-1.3-7.5-3zM4.5 8v4c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3V8M4.5 12v4c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3v-4" />
      <circle className="node" cx="12" cy="8" r="1.3" />
    </>
  ),
  ops: (
    <>
      <path d="M4.5 8h14.5M15.5 4.5 19 8l-3.5 3.5M19.5 16H5M8.5 12.5 5 16l3.5 3.5" />
      <circle className="node" cx="4.5" cy="8" r="1.7" />
      <circle className="node" cx="19.5" cy="16" r="1.7" />
    </>
  ),
  import: (
    <>
      <path className="soft" d="M4 15h16v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
      <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
      <path d="M12 5v9.5M8 11l4 4 4-4" />
      <circle className="node" cx="12" cy="4.3" r="1.5" />
    </>
  ),
  wallet: (
    <>
      <rect className="soft" x="3.5" y="6.5" width="17" height="13" rx="3" />
      <rect x="3.5" y="6.5" width="17" height="13" rx="3" />
      <path d="M6.5 6.5 15 4a1.5 1.5 0 0 1 1.9 1.1l.4 1.4" />
      <path d="M20.5 11.5h-4a2.25 2.25 0 0 0 0 4.5h4" />
      <circle className="node" cx="16.6" cy="13.75" r="1.1" />
    </>
  ),
  conc: (
    <>
      <circle className="soft" cx="9" cy="12" r="5.5" />
      <circle cx="9" cy="12" r="5.5" />
      <circle cx="15" cy="12" r="5.5" />
      <path d="m12.6 12.2 1.5 1.5 2.9-3.2" />
    </>
  ),
  finreport: (
    <>
      <path
        className="soft"
        d="M7 3.5h7l4.5 4.5v11a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5z"
      />
      <path d="M7 3.5h7l4.5 4.5v11a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5zM14 3.5V8h4.5" />
      <path d="m8.5 16 2.3-2.6 1.9 1.6 2.8-3.4" />
      <circle className="node" cx="15.5" cy="11.6" r="1.2" />
    </>
  ),
  reports: (
    <>
      <rect x="4" y="13" width="3.6" height="7" rx="1" />
      <rect x="10.2" y="9" width="3.6" height="11" rx="1" />
      <rect className="soft" x="16.4" y="5" width="3.6" height="15" rx="1" />
      <rect x="16.4" y="5" width="3.6" height="15" rx="1" />
      <circle className="node" cx="18.2" cy="8.6" r="1.1" />
    </>
  ),
  budget: (
    <>
      <path className="soft" d="M12 12V4a8 8 0 0 1 8 8z" />
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 12V3.5M12 12h8.5" />
      <circle className="node" cx="12" cy="12" r="1.7" />
    </>
  ),
  membership: (
    <>
      <rect className="soft" x="3" y="5" width="18" height="14" rx="3" />
      <rect x="3" y="5" width="18" height="14" rx="3" />
      <circle cx="8.5" cy="11" r="2" />
      <path d="M5.3 16c.5-1.7 1.8-2.6 3.2-2.6s2.7.9 3.2 2.6M14.5 10.5h3.5M14.5 13.5h2.5" />
    </>
  ),
  member: (
    <>
      <circle className="soft" cx="12" cy="8.5" r="3.4" />
      <circle cx="12" cy="8.5" r="3.4" />
      <path d="M5 20c0-3.9 3.1-6.5 7-6.5s7 2.6 7 6.5" />
      <circle className="node" cx="18.6" cy="5.4" r="1.5" />
    </>
  ),
  receipt: (
    <>
      <path className="soft" d="M6 3.5h12v17l-2.5-1.6-2.5 1.6-2.5-1.6L8.5 20.5 6 18.9z" />
      <path d="M6 3.5h12v17l-2.5-1.6-2.5 1.6-2.5-1.6L8.5 20.5 6 18.9z" />
      <path d="M9.2 8h5.6M9.2 11.5h3.2" />
      <circle className="node" cx="14.8" cy="14.6" r="1.4" />
    </>
  ),
  charge: (
    <>
      <circle className="soft" cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 8v8M8 12h8" />
      <circle className="node" cx="12" cy="12" r="1.1" />
    </>
  ),
  feeimport: (
    <>
      <rect className="soft" x="4" y="5.5" width="16" height="14.5" rx="2.5" />
      <rect x="4" y="5.5" width="16" height="14.5" rx="2.5" />
      <path d="M8.5 3.5v3.5M15.5 3.5v3.5M12 10.5v6M9.3 14l2.7 2.7 2.7-2.7" />
    </>
  ),
  bulk: (
    <>
      <path d="m4.5 6.6 1.4 1.4 2.6-2.8M4.5 12.1 5.9 13.5l2.6-2.8M11.5 7h8.5M11.5 12.5H20M11.5 18H20" />
      <circle className="node" cx="6" cy="18" r="1.5" />
    </>
  ),
  settings: (
    <>
      <path d="M4 6h7M17 6h3M4 12h3M13 12h7M4 18h9M19 18h1" />
      <circle className="soft" cx="14" cy="6" r="2.6" />
      <circle cx="14" cy="6" r="2.6" />
      <circle className="soft" cx="10" cy="12" r="2.6" />
      <circle cx="10" cy="12" r="2.6" />
      <circle className="soft" cx="16" cy="18" r="2.6" />
      <circle cx="16" cy="18" r="2.6" />
    </>
  ),
  company: (
    <>
      <rect className="soft" x="5" y="4" width="10" height="16" rx="1.5" />
      <path d="M5 20V5.5A1.5 1.5 0 0 1 6.5 4h7A1.5 1.5 0 0 1 15 5.5V20M15 9h2.5A1.5 1.5 0 0 1 19 10.5V20M3.5 20h17M8.2 8h1.6M11.2 8h1.6M8.2 12h1.6M11.2 12h1.6" />
      <circle className="node" cx="17" cy="14" r="1.1" />
    </>
  ),
  access: (
    <>
      <path className="soft" d="M12 3.5 19 6.5v5.2c0 4.2-2.8 7.2-7 8.8-4.2-1.6-7-4.6-7-8.8V6.5z" />
      <path d="M12 3.5 19 6.5v5.2c0 4.2-2.8 7.2-7 8.8-4.2-1.6-7-4.6-7-8.8V6.5z" />
      <circle cx="12" cy="10" r="2" />
      <path d="M8.6 15.5c.6-1.9 1.8-2.8 3.4-2.8s2.8.9 3.4 2.8" />
    </>
  ),
  catalogs: (
    <>
      <rect className="soft" x="4" y="4.5" width="16" height="4.5" rx="1.5" />
      <rect x="4" y="4.5" width="16" height="4.5" rx="1.5" />
      <rect x="4" y="10.5" width="16" height="4.5" rx="1.5" />
      <rect x="4" y="16.5" width="16" height="3.5" rx="1.5" />
      <circle className="node" cx="7.5" cy="6.75" r="1.1" />
    </>
  ),
  fx: (
    <>
      <circle className="soft" cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.5 15.5v-7M7.5 10.5l2-2 2 2M14.5 8.5v7M12.5 13.5l2 2 2-2" />
    </>
  ),
  payment: (
    <>
      <rect className="soft" x="3" y="6" width="18" height="12" rx="2.5" />
      <rect x="3" y="6" width="18" height="12" rx="2.5" />
      <path d="M3 10h18M11 14.5h4" />
      <circle className="node" cx="7" cy="14.5" r="1.2" />
    </>
  ),
  logistics: (
    <>
      <path className="soft" d="M2.5 7.5h11v9h-11z" />
      <path d="M2.5 7.5h11v9h-11zM13.5 10.5h4l3 3v3h-7" />
      <circle cx="7" cy="17.5" r="2" />
      <circle cx="17" cy="17.5" r="2" />
      <circle className="node" cx="7" cy="17.5" r=".8" />
    </>
  ),
  cube: (
    <>
      <path className="soft" d="M12 3 20 7.5 12 12 4 7.5z" />
      <path d="M12 3 20 7.5v9L12 21l-8-4.5v-9zM4 7.5 12 12l8-4.5M12 12v9" />
      <circle className="node" cx="12" cy="12" r="1.5" />
    </>
  ),
  stock: (
    <>
      <path className="soft" d="M12 4 3.5 8.5 12 13l8.5-4.5z" />
      <path d="M12 4 3.5 8.5 12 13l8.5-4.5zM3.5 12.5 12 17l8.5-4.5M3.5 16.5 12 21l8.5-4.5" />
      <circle className="node" cx="12" cy="8.5" r="1.3" />
    </>
  ),
  analysis: (
    <>
      <circle className="soft" cx="10.5" cy="10.5" r="6.5" />
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m15.4 15.4 4.6 4.6M7.6 12.6l2-2.2 1.6 1.2 2-2.6" />
      <circle className="node" cx="13.2" cy="8.8" r="1" />
    </>
  ),
  stocktake: (
    <>
      <rect className="soft" x="5.5" y="4.5" width="13" height="16" rx="2" />
      <rect x="5.5" y="4.5" width="13" height="16" rx="2" />
      <rect x="9" y="3" width="6" height="3.5" rx="1" />
      <path d="m8.4 11.5 1.4 1.4 2.4-2.6M14 12h2M12 16.5h4" />
      <circle className="node" cx="9" cy="16.5" r="1.1" />
    </>
  ),
  warehouse: (
    <>
      <path className="soft" d="M3 10 12 4l9 6v10H3z" />
      <path d="M3 10 12 4l9 6v10H3zM8 20v-7h8v7M8 16.5h8" />
      <circle className="node" cx="12" cy="9" r="1.2" />
    </>
  ),
  landing: (
    <>
      <path className="soft" d="M4 9h16v8.5A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5z" />
      <rect x="3.5" y="4" width="17" height="16" rx="2.5" />
      <path d="M3.5 9h17M7 14.5h6.5M7 17h3.5" />
      <circle className="node" cx="6.2" cy="6.5" r=".9" />
      <circle className="node" cx="9" cy="6.5" r=".9" />
      <circle cx="16.5" cy="14.6" r="1.7" />
    </>
  ),
  blog: (
    <>
      <rect className="soft" x="4" y="5" width="16" height="14" rx="2" />
      <rect x="4" y="5" width="16" height="14" rx="2" />
      <rect x="7" y="8" width="4.5" height="4" rx=".8" />
      <path d="M14 8.5h3M14 11.5h3M7 15.5h10" />
    </>
  ),
  edit: (
    <>
      <path className="soft" d="M5 19l1-4L16.5 4.6a2 2 0 0 1 2.9 2.8L8.9 17.9z" />
      <path d="M5 19l1-4L16.5 4.6a2 2 0 0 1 2.9 2.8L8.9 17.9zM14.5 6.6l2.9 2.9" />
      <circle className="node" cx="5.6" cy="18.4" r="1.2" />
    </>
  ),
  shield: (
    <>
      <path className="soft" d="M12 3.5 19 6.5v5.2c0 4.2-2.8 7.2-7 8.8-4.2-1.6-7-4.6-7-8.8V6.5z" />
      <path d="M12 3.5 19 6.5v5.2c0 4.2-2.8 7.2-7 8.8-4.2-1.6-7-4.6-7-8.8V6.5z" />
      <circle className="node" cx="12" cy="11.5" r="2" />
    </>
  ),
  orgs: (
    <>
      <rect className="soft" x="11.5" y="4" width="8.5" height="16" rx="1.5" />
      <path d="M11.5 20V5.5A1.5 1.5 0 0 1 13 4h5.5A1.5 1.5 0 0 1 20 5.5V20M11.5 10H6.5A1.5 1.5 0 0 0 5 11.5V20M3.5 20h17M14.5 8h.01M17.5 8h.01M14.5 12h.01M17.5 12h.01M8 14h.01M8 17h.01" />
      <circle className="node" cx="16" cy="16.5" r="1.2" />
    </>
  ),
  users: (
    <>
      <circle className="soft" cx="9" cy="8.5" r="3.2" />
      <circle cx="9" cy="8.5" r="3.2" />
      <path d="M3.5 19.5c0-3.2 2.4-5.4 5.5-5.4s5.5 2.2 5.5 5.4M15.6 5.6a2.8 2.8 0 0 1 0 5.2M17.5 14.4c1.9.6 3 2.3 3 5.1" />
    </>
  ),
  plans: (
    <>
      <path
        className="soft"
        d="M20 12.4 12.4 20a1.4 1.4 0 0 1-2 0L4 13.6a1.4 1.4 0 0 1 0-2L11.6 4H19a1 1 0 0 1 1 1z"
      />
      <path d="M20 12.4 12.4 20a1.4 1.4 0 0 1-2 0L4 13.6a1.4 1.4 0 0 1 0-2L11.6 4H19a1 1 0 0 1 1 1z" />
      <circle className="node" cx="15.5" cy="8.5" r="1.5" />
    </>
  ),
  trend: (
    <>
      <path d="M4 4.5v15h16M7.5 15l3.8-4 2.7 2.4 4-5.4" />
      <circle className="node" cx="18" cy="7.9" r="1.6" />
    </>
  ),
  globe: (
    <>
      <circle className="soft" cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c-3 2.6-3 14.4 0 17M12 3.5c3 2.6 3 14.4 0 17" />
      <circle className="node" cx="12" cy="12" r="1.3" />
    </>
  ),
  coin: (
    <>
      <circle className="soft" cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="8.5" />
      <path d="M14.3 9.4c-.5-.8-1.5-1.2-2.4-1.2-1.4 0-2.4.8-2.4 1.8 0 2.6 5 1.4 5 4 0 1-1 1.9-2.5 1.9-1.1 0-2.1-.5-2.7-1.4M12 6.7v1.5M12 15.8v1.5" />
    </>
  ),
  bank: (
    <>
      <path className="soft" d="M3.5 9.5 12 4l8.5 5.5z" />
      <path d="M3.5 9.5 12 4l8.5 5.5zM6.5 12.5v5M10.2 12.5v5M13.8 12.5v5M17.5 12.5v5M4 20.5h16" />
      <circle className="node" cx="12" cy="8.2" r="1.3" />
    </>
  ),
  tree: (
    <>
      <circle className="soft" cx="12" cy="5.5" r="2.4" />
      <circle cx="12" cy="5.5" r="2.4" />
      <circle cx="6" cy="18.5" r="2.4" />
      <circle cx="18" cy="18.5" r="2.4" />
      <path d="M12 8v4M6 16.1v-2.6c0-.8.7-1.5 1.5-1.5h9c.8 0 1.5.7 1.5 1.5v2.6" />
      <circle className="node" cx="12" cy="12" r="1.1" />
    </>
  ),
  cards: (
    <>
      <rect x="6" y="4" width="14" height="10" rx="2" />
      <rect className="soft" x="4" y="9" width="14" height="10" rx="2" />
      <rect x="4" y="9" width="14" height="10" rx="2" />
      <path d="M4 12.5h14" />
      <circle className="node" cx="7.5" cy="16" r="1.1" />
    </>
  ),
  medal: (
    <>
      <path d="M9 14 7.5 20.5l4.5-2.3 4.5 2.3L15 14" />
      <circle className="soft" cx="12" cy="9.5" r="5.5" />
      <circle cx="12" cy="9.5" r="5.5" />
      <circle className="node" cx="12" cy="9.5" r="1.8" />
    </>
  ),
  calendar: (
    <>
      <rect className="soft" x="4" y="5.5" width="16" height="14.5" rx="2.5" />
      <rect x="4" y="5.5" width="16" height="14.5" rx="2.5" />
      <path d="M4 10h16M8.5 3.5v3.5M15.5 3.5v3.5" />
      <circle className="node" cx="12" cy="15" r="1.7" />
    </>
  ),
  fields: (
    <>
      <rect className="soft" x="4" y="5" width="16" height="5.5" rx="1.5" />
      <rect x="4" y="5" width="16" height="5.5" rx="1.5" />
      <rect x="4" y="13.5" width="10" height="5.5" rx="1.5" />
      <path d="M18 14v5M15.5 16.5h5" />
    </>
  ),
  sign: (
    <>
      <path d="M4 20h16" />
      <path d="M5 15c1.6 0 2-5 3.6-5s.9 5 2.5 5 1.4-3.2 3-3.2 1 3.2 2.6 3.2H20" />
      <circle className="node" cx="18.5" cy="6.5" r="1.6" />
    </>
  ),
  doc: (
    <>
      <path
        className="soft"
        d="M7 3.5h7l4.5 4.5v11a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5z"
      />
      <path d="M7 3.5h7l4.5 4.5v11a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5zM14 3.5V8h4.5M8.5 13h7M8.5 16.5H13" />
    </>
  ),
  hash: (
    <>
      <path d="M9.2 4.5 7.8 19.5M16.2 4.5l-1.4 15M5 9h15M4 15h15" />
      <circle className="node" cx="12" cy="12" r="1.3" />
    </>
  ),
  register: (
    <>
      <rect className="soft" x="4" y="11" width="16" height="9" rx="2" />
      <rect x="4" y="11" width="16" height="9" rx="2" />
      <path d="M7 11V6.5A1.5 1.5 0 0 1 8.5 5h7A1.5 1.5 0 0 1 17 6.5V11" />
      <circle className="node" cx="8.5" cy="15.5" r="1" />
      <circle className="node" cx="12" cy="15.5" r="1" />
      <circle className="node" cx="15.5" cy="15.5" r="1" />
    </>
  ),
  cart: (
    <>
      <path className="soft" d="M6.8 8h11.7l-1.5 6.5H7.7z" />
      <path d="M3.5 5h2.2l1.9 9.5h9.4l1.5-6.5H6.6" />
      <circle className="node" cx="9.5" cy="18.7" r="1.5" />
      <circle className="node" cx="16.5" cy="18.7" r="1.5" />
    </>
  ),
  order: (
    <>
      <path d="M12.5 3.5H7A1.5 1.5 0 0 0 5.5 5v14A1.5 1.5 0 0 0 7 20.5h4" />
      <path className="soft" d="M12.5 3.5 17 8h-3.5a1 1 0 0 1-1-1z" />
      <path d="M12.5 3.5 17 8v1.5M8.5 12h5" />
      <path d="m13.5 20.5.7-2.8 5.2-5.2a1.4 1.4 0 0 1 2 2l-5.2 5.2z" />
    </>
  ),
  done: (
    <>
      <circle className="soft" cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="8.5" />
      <path d="m8.2 12.3 2.6 2.6 5-5.4" />
    </>
  ),
  warning: (
    <>
      <path className="soft" d="M12 4 21 19.5H3z" />
      <path d="M12 4 21 19.5H3zM12 10v4.2" />
      <circle className="node" cx="12" cy="16.8" r="1.1" />
    </>
  ),
  list: (
    <>
      <path d="M10 6h10M10 12h10M10 18h10" />
      <circle className="node" cx="5.5" cy="6" r="1.4" />
      <circle className="node" cx="5.5" cy="12" r="1.4" />
      <circle className="node" cx="5.5" cy="18" r="1.4" />
    </>
  ),
  lock: (
    <>
      <rect className="soft" x="5" y="10.5" width="14" height="10" rx="2.5" />
      <rect x="5" y="10.5" width="14" height="10" rx="2.5" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
      <circle className="node" cx="12" cy="15.5" r="1.4" />
    </>
  ),
  income: (
    <>
      <circle className="soft" cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 16.2V8.4M8.8 11.5 12 8.3l3.2 3.2" />
    </>
  ),
  expense: (
    <>
      <circle className="soft" cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.8v7.8M8.8 12.5l3.2 3.2 3.2-3.2" />
    </>
  ),
  swap: (
    <>
      <path d="M18.6 9.2A7 7 0 0 0 6.3 7.6M5.4 14.8a7 7 0 0 0 12.3 1.6M6 4v3.8h3.8M18 20v-3.8h-3.8" />
      <circle className="node" cx="12" cy="12" r="1.5" />
    </>
  ),
  cheque: (
    <>
      <rect className="soft" x="3" y="7" width="18" height="10" rx="2" />
      <rect x="3" y="7" width="18" height="10" rx="2" />
      <path d="M6.5 11h4.5M6.5 14h6" />
      <circle className="node" cx="17" cy="12.6" r="1.6" />
    </>
  ),
  creditnote: (
    <>
      <path
        className="soft"
        d="M7 3.5h7l4.5 4.5v11a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5z"
      />
      <path d="M7 3.5h7l4.5 4.5v11a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5zM14 3.5V8h4.5M12 11.5v5M9.5 14h5" />
    </>
  ),
  debitnote: (
    <>
      <path
        className="soft"
        d="M7 3.5h7l4.5 4.5v11a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5z"
      />
      <path d="M7 3.5h7l4.5 4.5v11a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5zM14 3.5V8h4.5M9.5 14h5" />
    </>
  ),
  hourglass: (
    <>
      <path className="soft" d="M8 4h8c0 3.4-2.4 4.7-4 8-1.6-3.3-4-4.6-4-8z" />
      <path d="M7 4h10M7 20h10M8 4c0 4 3 5 4 8-1 3-4 4-4 8M16 4c0 4-3 5-4 8 1 3 4 4 4 8" />
      <circle className="node" cx="12" cy="17.6" r="1" />
    </>
  ),
  calcheck: (
    <>
      <rect className="soft" x="4" y="5.5" width="16" height="14.5" rx="2.5" />
      <rect x="4" y="5.5" width="16" height="14.5" rx="2.5" />
      <path d="M4 10h16M8.5 3.5v3.5M15.5 3.5v3.5M9.3 15l1.9 1.9 3.5-3.7" />
    </>
  ),
  useradd: (
    <>
      <circle className="soft" cx="10" cy="8.5" r="3.3" />
      <circle cx="10" cy="8.5" r="3.3" />
      <path d="M3.5 20c0-3.5 2.8-5.7 6.5-5.7.9 0 1.7.1 2.4.4M18 12.5v6M15 15.5h6" />
    </>
  ),
  adjust: (
    <>
      <path d="M8 20V6M4.8 9.2 8 6l3.2 3.2M16 4v14M12.8 14.8 16 18l3.2-3.2" />
      <circle className="node" cx="8" cy="17" r="1.4" />
      <circle className="node" cx="16" cy="7" r="1.4" />
    </>
  ),
};

export const KYRA_ICON_NAMES = Object.keys(GLYPHS) as KyraIconName[];

export type KyraIconProps = {
  name: KyraIconName;
  size?: number | string;
  className?: string;
  title?: string;
} & Omit<SVGProps<SVGSVGElement>, 'name' | 'width' | 'height' | 'viewBox' | 'children'>;

export function KyraIcon({ name, size = 18, className, title, ...rest }: KyraIconProps) {
  return (
    <svg
      {...rest}
      className={className ? `kyra-icon ${className}` : 'kyra-icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {GLYPHS[name]}
    </svg>
  );
}

/** Misma firma que los iconos de Phosphor (`size`, `weight`, `className`) para poder usarlos en las mismas listas. */
export type IconLikeProps = { size?: number | string; weight?: IconWeight; className?: string };
export type IconLike = ComponentType<IconLikeProps>;

function makeIcon(name: KyraIconName): IconLike {
  function Icon({ size, className }: IconLikeProps) {
    return <KyraIcon name={name} size={size} className={className} />;
  }
  Icon.displayName = `KyraIcon(${name})`;
  return Icon;
}

export const kyraIcons = Object.fromEntries(
  KYRA_ICON_NAMES.map((name) => [name, makeIcon(name)]),
) as Record<KyraIconName, IconLike>;
